import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { UserStatus } from "@sitescene/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { User } from "@/types/users";

import { setLogoutHandler } from "@/api/logout-manager";
import { setTokenGetter } from "@/api/token-manager";
import { prepareSessionStorageForLogout } from "@/features/auth/auth-session-storage";
import { clearAllStoredEventFilters } from "@/features/events/utils/stored-events-filters";
import { clearAllListRestoreSnapshots } from "@/features/list-restore";
import { ExternalClient } from "@/types/common";
import { UserRole } from "@/types/users";
import { resolveOrganizationName } from "@/utils/organization";

/**
 * Authentication context props
 */
export interface AuthContextProps {
	/** Kinde SDK initialization / auth error (e.g. token refresh failure) */
	authError?: string;
	authUser: null | User;
	isAuthenticated: boolean;
	isLoadingAuth: boolean;
	login: (options?: {
		lang?: string;
		loginHint?: string;
		orgCode?: string;
	}) => Promise<void>;
	logout: () => Promise<void>;
	orgCode: null | string;
	/** Kinde `org_name` claim when present on the token */
	orgName: null | string;
	register: (options?: {
		lang?: string;
		loginHint?: string;
		orgCode?: string;
	}) => Promise<void>;
}

/**
 * Map Kinde user profile to our User type
 */
const mapKindeUserToUser = (
	kindeUser: ReturnType<typeof useKindeAuth>["user"],
): null | User => {
	if (!kindeUser) {
		return null;
	}

	// Extract roles from permissions or use default
	// Kinde provides permissions, we'll map them to roles
	// You may need to adjust this mapping based on your Kinde configuration
	const roles: UserRole[] = [];

	// If Kinde provides roles in the token, extract them
	// Otherwise, default to USER role
	if (kindeUser.id) {
		// Default role - you can customize this based on Kinde permissions
		roles.push(UserRole.USER);
	}

	const now = new Date();
	const email = kindeUser.email || "";
	const emailHandle = email.split("@")[0] || kindeUser.id || "";

	return {
		createdAt: now,
		email,
		external: {
			handle: emailHandle,
			id: kindeUser.id || "",
			name: ExternalClient.NIGHT_WATCH, // Default, adjust as needed
		},
		firstName: kindeUser.givenName || "",
		id: kindeUser.id || "",
		isSuperUser: false, // Set based on your Kinde permissions/roles if needed
		lastName: kindeUser.familyName || "",
		notificationTypes: [],
		phone: undefined, // Phone number not available in Kinde user profile by default
		picture: kindeUser.picture || undefined,
		roles,
		status: UserStatus.Active,
		updatedAt: now,
	};
};

/**
 * Convenience hook that wraps Kinde auth and provides compatible interface
 */
export const useAuth = (): AuthContextProps => {
	const {
		error: kindeError,
		getAccessToken: kindeGetAccessToken,
		getClaim,
		getOrganization,
		isAuthenticated,
		isLoading: isLoadingAuth,
		login: kindeLogin,
		logout: kindeLogout,
		register: kindeRegister,
		user: kindeUser,
	} = useKindeAuth();

	// Note: Permissions are available via usePermissions() hook directly
	// We don't expose them here to keep the auth context focused

	// Resolve the current Kinde organization so consumers can use it synchronously
	const [orgCode, setOrgCode] = useState<null | string>(null);
	const [orgName, setOrgName] = useState<null | string>(null);

	// Keep latest Kinde helpers in refs so identity changes (not function identity)
	// drive the org-name fetch. Kinde may return a new getClaim/getOrganization
	// each render.
	const getClaimRef = useRef(getClaim);
	const getOrganizationRef = useRef(getOrganization);
	useEffect(() => {
		getClaimRef.current = getClaim;
		getOrganizationRef.current = getOrganization;
	}, [getClaim, getOrganization]);

	useEffect(() => {
		let cancelled = false;

		void (async () => {
			if (!isAuthenticated) {
				if (!cancelled) {
					setOrgCode(null);
					setOrgName(null);
				}
				return;
			}

			const [code, name] = await Promise.all([
				getOrganizationRef.current(),
				resolveOrganizationName(getClaimRef.current),
			]);
			if (cancelled) {
				return;
			}
			setOrgCode(code);
			setOrgName(name ?? null);
		})();

		return () => {
			cancelled = true;
		};
	}, [isAuthenticated, kindeUser?.id]);

	// Store kindeGetAccessToken in a ref to avoid re-running the effect when it changes
	// This prevents blocking renders while still always using the latest function
	const kindeGetAccessTokenRef = useRef(kindeGetAccessToken);
	useEffect(() => {
		kindeGetAccessTokenRef.current = kindeGetAccessToken;
	}, [kindeGetAccessToken]);

	// Set up token getter for API client
	// Only run once on mount to avoid blocking renders
	useEffect(() => {
		setTokenGetter(async () => {
			try {
				const token = await kindeGetAccessTokenRef.current();
				return token ?? null;
			} catch (error) {
				console.error("Error getting access token:", error);
				return null;
			}
		});
	}, []); // Empty dependency array - only run once on mount

	// Set up logout handler for API client (to handle 401/403 errors)
	useEffect(() => {
		setLogoutHandler(async () => {
			await kindeLogout();
		});
	}, [kindeLogout]);

	// Map Kinde user to our User type so we can use it in the app with more data
	const authUser = useMemo(() => {
		return mapKindeUserToUser(kindeUser);
	}, [kindeUser]);

	// Login function compatible with our interface
	const login = useCallback(
		async (options?: {
			lang?: string;
			loginHint?: string;
			orgCode?: string;
		}) => {
			try {
				await kindeLogin({
					lang: options?.lang,
					loginHint: options?.loginHint,
					orgCode: options?.orgCode,
				});
			} catch (error) {
				console.error("Login error:", error);
				throw error;
			}
		},
		[kindeLogin],
	);

	// Register function compatible with our interface
	const register = useCallback(
		async (options?: {
			lang?: string;
			loginHint?: string;
			orgCode?: string;
		}) => {
			try {
				await kindeRegister({
					lang: options?.lang,
					loginHint: options?.loginHint,
					orgCode: options?.orgCode,
				});
			} catch (error) {
				console.error("Register error:", error);
				throw error;
			}
		},
		[kindeRegister],
	);

	// Logout function
	const logout = useCallback(async () => {
		try {
			// Prepare session storage for logout by clearing the redirect and setting the "suppress" flag so AuthGuard does not re-save the current URL when auth drops before navigation.
			prepareSessionStorageForLogout();
			clearAllStoredEventFilters();
			clearAllListRestoreSnapshots();
			await kindeLogout();
		} catch (error) {
			console.error("Logout error:", error);
			throw error;
		}
	}, [kindeLogout]);

	return useMemo(
		() => ({
			authError: kindeError,
			authUser,
			isAuthenticated,
			isLoadingAuth,
			login,
			logout,
			orgCode,
			orgName,
			register,
		}),
		[
			authUser,
			isLoadingAuth,
			isAuthenticated,
			kindeError,
			login,
			register,
			logout,
			orgCode,
			orgName,
		],
	);
};
